import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _StringUtils;
/*
 *                     NOTICE
 *            COPYRIGHT 2019 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

// Type declarations are used at compile-time as 'headers' only (does NOT include the files)
// Be very judicious about what is imported into StringUtils as can introduce circular dependencies with
// other static helper classes (do NOT import 'Assert')

import { Assert } from "abweb/utils/Assert";
import * as DOMPurify from "dompurify";
export class StringUtils {
  constructor() {
    // static class, can't create an instance
  }

  /**
   * Capitalize the first character in a string
   * @param str
   */
  static capitalizeFirst(str) {
    if (str) {
      return str.charAt(0).toUpperCase() + str.substring(1);
    } else {
      return "";
    }
  }

  /**
   * Inserts backslashes before any special characters so that they are treated as literals in a RegExp.
   * @param str String containing special regexp characters to escape
   */
  static regExpEscape(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }
  static escapeRegExp(val) {
    // This is Drew's escapeRegEx function, moved below Anthony's regExpEscape function for easier comparison.
    // MH-22004 will unify these two slightly different implementations
    return val.replace(/[.*+\-?^${}()|[\]\\]/g, `\\$&`); // $& means the whole matched string
  }
  /**@param substring String or a RegEx  
   * @return number of times the substring occurs within in the str parameter */
  static countSubstrings(str, substring) {
    if (typeof substring === "string") {
      substring = new RegExp(StringUtils.escapeRegExp(substring), "g");
    } else !substring.global;
    {
      substring = new RegExp(substring, "g");
    }
    // substring regex needs to be global to count higher than 1!
    return (str.match(substring) || []).length;
  }

  /**
   * Compares the two strings, ignoring case and trailing/leading white space.
   *
   * Note: null/undefined is equal to null/undefined but otherwise, both must be
   * non-null and equal for this function to return true.
   *
   * @param str1
   * @param str2
   * @returns {boolean}
   */
  static equalsIgnoreCase(str1, str2) {
    if (!str1 && !str2) {
      return true;
    } else if (!str1 || !str2) {
      return false;
    } else {
      return str1.toLowerCase().trim() === str2.toLowerCase().trim();
    }
  }
  static compareStrings(str1, str2) {
    let result = 0;
    if (str1 && !str2) {
      result = 1;
    } else if (!str1 && str2) {
      result = -1;
    } else if (str1 !== str2) {
      result = str1 > str2 ? 1 : -1;
    }
    return result;
  }
  static compareStringsIgnoreCase(str1, str2) {
    if (str1 && str2) return str1.toLowerCase() > str2.toLowerCase() ? 1 : -1;else if (str1 && !str2) return 1;else if (!str1 && str2) return -1;else return 0;
  }

  /**
   * Compares two strings, considering them equal if they are:
   * <ul>
   *   <li>both null or undefined (but not necessarily the same)</li>
   *   <li>both valued and the same value</li>
   * </ul>
   * @param s1    the first string
   * @param s2    the second string
   * @returns {false | boolean}
   */
  static stringsAreEqual(s1, s2) {
    if (s1 != null && s2 != null) {
      return s1 === s2;
    }
    return s1 == null && s2 == null;
  }
  static stringNthIndex(str1, match, nth) {
    if (nth <= 0) {
      return -1;
    }
    let index = 0;
    while (true) {
      index = str1.indexOf(match, index);
      if (index < 0 || --nth === 0) {
        return index;
      }
      //LOG.log(`nth: ${nth}, index: ${index}`);
      index += match.length; // skip over matched substr for next try
    }
  }

  /**
   * Very lightweight version of sprintf with 0-based arg parameters such as {2}.  Does not support parameter escaping.
   * <p>Example usage: <code>
   *     &nbsp;&nbsp;&nbsp; sprintf("My {0} args {1} and reversed args {0}, {1}", "hello", "world");
   * </code>
   * @throws an exception if any parameter can not be resolved.
   */
  static sprintf(msg, ...msgArgs) {
    return msg.replace(/\{(\d+)\}/g, (m, n) => {
      // Fails hard and fast on Programmer Error of n being an invalid index
      // ('TypeError: 'undefined' is not an object)
      return msgArgs[n] === null ? "" : msgArgs[n].toString();
    });

    // See sprintf-js.d.ts for complete doc of args, including formatting options (or https://www.npmjs.com/package/sprintf-js)
    // However, only gave finer-grained control for formatting numbers (padding, precision), so not really that useful
    // and syntax is gross (e.g. %2$s instead of {2})
    //return sprintf.vsprintf(lookup, msgArgs);
  }

  /**
   * Same as sprintf but if parameter is unresolved it will leave it instead of throwing an exception.
   * Recommend using sprintf instead as it fails fast on programmer error (unresolved parameters).
   * Only use sprintfUnresolvedParams if need to support unresolved parameters.
   */
  static sprintfUnresolvedParams(msg, ...msgArgs) {
    return msg.replace(/\{(\d+)\}/g, (m, n) => {
      return n >= msgArgs.length ? m : msgArgs[n] === null ? "" : msgArgs[n].toString();
    });
  }

  /**
   * Tests if a string starts with a particular substring.
   * Most callers should use the builtin String::startsWith method instead.
   * Differs from builtin string 'startsWith' in that handles null type for str and searchString args.
   * @param str String to search.
   * @param searchString String to search for.
   * @returns {boolean} true if str starts with searchString, false otherwise.
   */
  static startsWith(str, searchString) {
    // if searchString is empty string, below always returns true
    // return !!(searchString && str?.startsWith(searchString));
    if (!str || !searchString)
      // null, undefined, or empty string
      {
        return false;
      }
    return str.indexOf(searchString) === 0;
  }

  /**
   * Tests if a string ends with a particular substring.
   * Most callers should use the builtin String::endsWith method instead.
   * @param str String to search.
   * @param searchString String to search for.
   * @returns {boolean} true if str ends with searchString, false otherwise.
   */
  static endsWith(str, searchString) {
    // if searchString is empty string, below always returns true
    // return !!(searchString && str?.endsWith(searchString));
    if (!str || !searchString)
      // null, undefined, or empty string
      {
        return false;
      }
    const lastIndex = str.length - searchString.length;
    return lastIndex >= 0 && str.substr(lastIndex) === searchString;
  }

  /**
   * Tests if a string contain a particular substring.
   * Most callers should use the builtin String::includes method instead.
   * Differs from builtin string 'includes' in that handles case where str can be null
   * @param str
   * @param searchVal
   */
  static contains(str, searchVal) {
    return str != null && str.includes(searchVal);
  }
  static containsIgnoreCase(str, searchVal) {
    if (str === null) return false;
    if (!searchVal) return true;
    return this.contains(str.toLowerCase(), searchVal.toLowerCase());
  }
  static lastIndexOfOrThrow(str, searchExpr) {
    const index = str.lastIndexOf(searchExpr);
    if (index < 0) {
      throw new Error(`Unable to find required '${searchExpr}' substring in string '${str}'`);
    }
    return index;
  }

  /**
   * Returns the string provided but with all white space characters removed. Useful for testing
   * and avoiding test fragility due to white space.
   *
   * @param str
   * @returns {string}
   */
  static removeAllWhiteSpace(str) {
    if (!str) {
      return str;
    }
    return str.replace(/\s/g, "");
  }

  /*
    A space sandwich is defined as <zero or more spaces> + <zero or more non-whitespace characters> + <zero or more spaces>
    This function is the equivalent of trim() with space excluded from the set of whitespace characters
    This function is useful if leading/trailing spaces are considered part of a valid input
   */

  static isNullOrEmpty(str, trimWhitespace = false) {
    return str == null || (trimWhitespace ? str.trim() : str).length === 0;
  }

  /**
   * Utility function that tests that the specified string is not null, not undefined, and not empty.
   *
   * @param str The string to test
   * @param trimWhitespace Optionally trim whitespace, which is `false` by default.
   * @return `true` if the string is not null, not undefined, and not empty; `false` otherwise.
   * @example
   *   // the type assertion "str is string" provides a hint to the compiler so that
   *   // the following works
   *   if (StringUtils.isNotNullOrEmpty(myString)) {
   *     return myString.length;
   *   }
   */
  static isNotNullOrEmpty(str, trimWhitespace = false) {
    return !StringUtils.isNullOrEmpty(str, trimWhitespace);
  }
  static defaultValueIfNull(str, defaultValue) {
    return str === undefined || str === null ? defaultValue : str;
  }

  /**
   * TODO - merge emptyIfNull and emptyIfNull2 and make sure nothing breaks
   */
  static emptyIfNull(str) {
    return str === null ? "" : str;
  }
  static emptyIfNullOrUndefined(str) {
    if (str === undefined || str === null) {
      return "";
    }
    return str;
  }

  /**
   * TODO - merge emptyIfNull and emptyIfNull2 and make sure nothing breaks
   */
  static emptyIfNull2(str) {
    return str == null ? "" : str;
  }

  /**
   * Returns null if the string is empty or null.
   * Otherwise returns the string itself.
   * @param {string | null} str
   * @returns {string | null}
   */
  static nullIfEmpty(str) {
    if (str == null || str.length === 0) {
      return null;
    }
    return str;
  }

  /**
   * Little construct to substitue a default value
   * when a string is empty, null, or undefined.
   */
  static nonEmptyDefault(str, defaultValue) {
    return str != null && str.length > 0 ? str : defaultValue;
  }

  /**
   * Take a string that may be null, and either throw if null,
   * or return the non-null string.
   * @param str
   * @returns {string|null}
   */
  static throwIfNull(str) {
    if (str === null) throw new Error(`Unexpected null string`);
    return str;
  }
  static throwIfNullOrEmpty(string) {
    Assert.assert(string != null && string.length > 0);
    return string;
  }

  /**
   * Returns a string abbreviation for a number with a given number of significant digits
   *
   * @param aNumber      The number to display, as a number or as a string
   * @param sigDigits    (optional)    Number of significant digits to show.  If omitted, or if less than 3, defaults to 3
   * @param iteration    (not for use) Used internally to count how many groups of 1000
   * @returns the abbreviation.
   */
  static abbreviateNumber(aNumber, sigDigits = 0, iteration = 0) {
    // Initializations
    let numberAsNumber;
    const unitsStrings = ["", "k", "M", "G", "T", "P", "E", "Z", "Y"];
    numberAsNumber = typeof aNumber === "string" ? Number(aNumber) : aNumber;
    if (sigDigits < 3) {
      sigDigits = 3;
    }
    if (iteration === 0) {
      // This is a special case to show numbers that are < 1000 without decimal points
      // as a straight number i.e. I don't want 12 to render as 12.0...
      if (numberAsNumber < 1000 && numberAsNumber === Math.floor(numberAsNumber)) {
        return String(numberAsNumber); // Set sig digits correctly
      }
    }
    if (numberAsNumber < 1000) {
      // Get the units, based on which iteration we're on
      const myChar = unitsStrings[iteration];
      // Figure out the whole and fractional part
      const wholePart = Math.floor(numberAsNumber);
      const fractionalPart = numberAsNumber - wholePart;
      // Numbers in this rank will now be less than 1000
      if (numberAsNumber < 10) {
        return wholePart + "." + Math.floor(fractionalPart * Math.pow(10, sigDigits - 1)) + myChar;
      }
      if (numberAsNumber < 100) {
        return wholePart + "." + Math.floor(fractionalPart * Math.pow(10, sigDigits - 2)) + myChar;
      }
      // Here, number is < 1000 -- b/c of the if it's in
      return wholePart + (sigDigits > 3 ? "." + Math.floor(fractionalPart * Math.pow(10, sigDigits - 3)) : "") + myChar;
    }
    // If bigger than 1000, then we have to try again in the next block of units.
    return this.abbreviateNumber(numberAsNumber / 1000, sigDigits, iteration + 1);
  }

  /**
   * Returns the index of the first occurrence of any of the characters in the <code>chars Array</code>.
   * Returns -1 if no matching character is found.
   */
  static indexOfAny(str, chars, startIndex = 0) {
    for (let i = startIndex; i < str.length; i++) {
      const ch = str[i];
      if (chars.indexOf(ch) !== -1) {
        return i;
      }
    }
    return -1;
  }

  /**
   * Returns the last index of the occurrence of any of the characters in the <code>chars Array</code>.
   * @param str String to search.
   * @param chars Array of characters to search for.
   * @param startIndex Zero-based start index in <code>s</code> to start the search. @default 0x7fffffff
   * @return Zero-based index of last character from  <code>chars</code> found in <code>s</code>.
   * Returns -1 if no character is found.
   */
  static lastIndexOfAny(str, chars, startIndex = 0x7fffffff) {
    let index = -1;
    let temp;
    for (let n = 0; n < chars.length; n++) {
      temp = str.lastIndexOf(chars[n], startIndex);
      if (temp >= 0) {
        index = Math.max(index, temp);
        if (index === startIndex) {
          return index;
        }
      }
    }
    return index;
  }

  /**
   * Return the portion of a string before the first match.
   * Degenerate case: If a match isn't found, the empty string is returned.
   * @param str The string to search.
   * @param match What to match.
   */
  static beforeFirst(str, match) {
    return str.substring(0, str.indexOf(match));
  }

  /**
   * Return the portion of a string up to the first match.
   * Degenerate case: If a match isn't found, the whole string is returned.
   * @param str The string to search.
   * @param match What to match.
   */
  static upToFirst(str, match) {
    if (str.indexOf(match) === -1) {
      return str;
    }
    return str.substring(0, str.indexOf(match));
  }

  /**
   * Return the portion of a string before the last match.
   * Degenerate case: If a match isn't found, the empty string is returned.
   * @param str The string to search.
   * @param match What to match.
   */
  static beforeLast(str, match) {
    return str.substring(0, str.lastIndexOf(match));
  }

  /**
   * Return the portion of a string after the first match.
   * Degenerate case: If a match isn't found, the entire string is returned.
   * @param str The string to search.
   * @param match What to match.
   */
  static afterFirst(str, match) {
    const matchIndex = str.indexOf(match);
    if (matchIndex === -1) return str;else return str.substring(matchIndex + match.length);
  }

  /**
   * Return the portion of a string after the first match.
   * Degenerate case: If a match isn't found, the empty string is returned.
   * @param str The string to search.
   * @param match What to match.
   */
  static remainderAfterFirst(str, match) {
    const matchIndex = str.indexOf(match);
    if (matchIndex === -1) return "";else return str.substring(matchIndex + match.length);
  }

  /**
   * Return the portion of a string after the last match.
   * Degenerate case: If a match isn't found, the entire string is returned.
   * @param str The string to search.
   * @param match What to match.
   */
  static afterLast(str, match) {
    const matchIndex = str.lastIndexOf(match);
    if (matchIndex === -1) return str;else return str.substring(matchIndex + match.length);
  }

  /**
   * Takes a set of strings of css class names and arrays of class name parts and makes a css class string out of them while
   * removing duplicates.  Accepts a set of input arguments which it does not modify. The arguments are combined based on what
   * type they are, either string or array.
   *
   * cssClassStr("foo bar baz") => "foo bar baz"
   * cssClassStr("foo", "bar", "baz") => "foo bar baz"
   * cssClassStr("foo", ["bar", "bar"], "baz buz") => "foo bar-bar baz buz"
   * cssClassStr(0, 1, false, "two", undefined, "three", null, "four", "", "five", [undefined, 6, null, 0, false, "6"]) => "0 1 two three four five 6-0-6"
   *
   * @param classes Array of css classname strings including one or more classes, or an array of css classname parts to be joined with "-".
   * @returns a string formatted as css class names
   */
  static cssClassStr(...classes) {
    // It's a value if it's the number 0 or a non-empty string or it's truthy but not boolean true
    let isValue = v => {
      return v === 0 || typeof v === "string" && v.trim().length > 0 || typeof v !== "string" && v && v !== true;
    };
    let aClasses = [];
    // Don't use for..of here, because kendo.data.ObservableArray doesn't support it.
    for (let i = 0; i < classes.length; i++) {
      if (Array.isArray(classes[i])) {
        // If this element is an array then strip out the falsey parts, join the rest with "-" then add it to the result
        aClasses.push(classes[i].filter(isValue).join("-"));
      } else if (isValue(classes[i])) {
        // If non-array then make it a string, trim it, split it a css classname string with one or many classes in it and add them in
        aClasses = aClasses.concat(String(classes[i]).split(" "));
      }
    }
    // Remove empty names and duplicates
    return aClasses.filter(function (v, i, a) {
      return v !== "" && a.indexOf(v) === i;
    }).join(" ");
  }

  /**
   * Counts the number of tokens in a string.
   * @param str String to search
   * @param token Token to find
   * @returns count of tokens
   */
  static getTokenCount(str, token) {
    if (StringUtils.isNullOrEmpty(str) || StringUtils.isNullOrEmpty(token)) {
      return 0;
    }
    let count = 0;
    let index = 0;
    while (true) {
      index = str.indexOf(token, index);
      if (index >= 0) {
        count++;
        index += token.length;
      } else {
        break;
      }
    }
    return count;
  }

  /*
    Separate words using the capitalized letters as the delimiter
    Ex. camelCase to Camel Case
   */
  static separateCamelCaseIntoWords(str) {
    const re = new RegExp("([A-Z])", "g");
    str = str.replace(re, (match, captureGroup) => {
      return " " + captureGroup;
    });
    return StringUtils.capitalizeFirst(str);
  }
  static capitalizeAbbreviations(str) {
    const abbreviations = ["cc", "db", "jdbc", "cpu"];
    const pattern = abbreviations.join("|");
    const re = new RegExp(`(${pattern})`, "gi");
    return str.replace(re, (match, captureGroup) => {
      return captureGroup.toUpperCase();
    });
  }
  static snakeToCamelCase(snake) {
    let pieces = snake.split("_").map(s => {
      if (s === "") {
        return "";
      }
      let s1 = s.charAt(0).toLocaleUpperCase() + (s.length === 1 ? "" : s.substr(1).toLocaleLowerCase());
      return s1;
    });
    return pieces.join("");
  }
  static kebabToCamelCase(kebab) {
    return StringUtils.snakeToCamelCase(kebab.replace("-", "_"));
  }
  static camelCase(x) {
    let calls = 0;
    return x.toLowerCase() // lowcase everything
    .replace(/^[^A-Za-z0-9]*/, "") // trim off leading nonletters, if any
    .replace(/(?:^[A-Za-z0-9])|(?:[^A-Za-z0-9][A-Za-z0-9])/g, match => {
      return calls++ === 0 ? match : match.toLocaleUpperCase();
    }) // 2nd, 3rd, 4th... words are upcased
    .replace(/[^A-Za-z0-9]/g, ""); // remove remaining nonletters
  }
  static toUTF8Array(str, indexToPos) {
    let utf8 = [];
    for (let i = 0; i < str.length; i++) {
      let charcode = str.charCodeAt(i);
      if (charcode < 0x80) {
        utf8.push(charcode);
        if (indexToPos !== undefined) {
          indexToPos.push(i);
        }
      } else if (charcode < 0x800) {
        utf8.push(0xc0 | charcode >> 6, 0x80 | charcode & 0x3f);
        if (indexToPos !== undefined) {
          indexToPos.push(i, i);
        }
      } else if (charcode < 0xd800 || charcode >= 0xe000) {
        utf8.push(0xe0 | charcode >> 12, 0x80 | charcode >> 6 & 0x3f, 0x80 | charcode & 0x3f);
        if (indexToPos !== undefined) {
          indexToPos.push(i, i, i);
        }
      } else
        // surrogate pair
        {
          i++;
          // UTF-16 encodes 0x10000-0x10FFFF by
          // subtracting 0x10000 and splitting the
          // 20 bits of 0x0-0xFFFFF into two halves
          charcode = 0x10000 + ((charcode & 0x3ff) << 10 | str.charCodeAt(i) & 0x3ff);
          utf8.push(0xf0 | charcode >> 18, 0x80 | charcode >> 12 & 0x3f, 0x80 | charcode >> 6 & 0x3f, 0x80 | charcode & 0x3f);
          if (indexToPos !== undefined) {
            indexToPos.push(i - 1, i - 1, i, i);
          }
        }
    }
    return utf8;
  }

  /**
   * Checks if the input string is longer than the given limit and truncates it if necessary.
   * If the string is indeed truncated, an optional "ending" is added to it.
   * @param {string} s - string to truncate if needed
   * @param {number} limit - maximum number of characters to take from the input string. If undefined
   * or <= 0 - don't truncate.
   * @param {string} ending - optional string to append to the truncated string. The default value
   * is "...". Note that ending is only appended if the string is truncated.
   * @returns {string} if the input string's length is less or equal to the given limit the original
   * string is returned; otherwise, the truncated string with the optional "ending" is returned
   */
  static truncateIfNeeded(s, limit, ending = "...") {
    if (!s) {
      return "";
    } else if (!limit || limit <= 0) {
      return s;
    } else {
      const endingLength = ending ? ending.length : 0;
      return s.length <= limit + endingLength ? s : s.substr(0, limit) + ending;
    }
  }

  /**
   * Checks if the input string is longer than the given limit and truncates it if necessary.
   * If the string is indeed truncated, an optional "middle" is inserted into it.
   * @param {string} s - string to truncate if needed
   * @param {number} limit - maximum number of characters to take from the input string. If undefined
   * or <= 0 - don't truncate.
   * @param {string} middle - optional string to insert into the truncated string. The default value
   * is "...". Note that middle is only inserted if the string is truncated.
   * @returns {string} if the input string's length is less or equal to the given limit the original
   * string is returned; otherwise, the truncated string with the optional "middle" is returned
   */
  static truncateMiddleIfNeeded(s, limit, middle = "...") {
    if (!s) {
      return "";
    } else if (!limit || limit <= 0) {
      return s;
    } else {
      const middleLength = middle ? middle.length : 0;
      return s.length <= limit + middleLength ? s : s.substr(0, limit / 2) + middle + s.substr(s.length - limit / 2);
    }
  }

  /**
   * Determine if a string consists only whitespace.
   */
  static isWhiteSpaceOnly(str) {
    return !/\S/.test(str);
  }

  /**
   * Determine if a char is one that can be used in a word, for some notion of word across different languages.
   * Numeric, whitespace, and punctuation chars are not considered word-ish.
   */
  static isWordishChar(ch) {
    if ("0" <= ch && ch <= "9") return false;else if (StringUtils.isWhiteSpaceOnly(ch)) return false;else if (StringUtils.isPunctuationChar(ch)) return false;else return true;
  }

  /**
   * Determine if a char is either a number or one that can be used in a word, for some notion of word across different languages.
   */
  static isWordishOrNumericChar(ch) {
    if ("0" <= ch && ch <= "9") return true;else return StringUtils.isWordishChar(ch);
  }

  /**
   * Punctuations chars include symbols, parens, etc.
   * Underscore is not considered a punctuation char.
   */
  static isPunctuationChar(ch) {
    const punct_chars = '~!@#$%^&*()+{}|:"<>?`-=[]\\;\',./';
    return punct_chars.indexOf(ch) !== -1;
  }

  /**
   * Find the start and end indexes of the
   * longest substring composed of only alphanumeric chars that include the char at index.
   * For example, to find the indexes for "strFun1" within "x +strFun1()"
   * Return as a {start, end} object.
   */
  static getAlphaNumericSubstringIndexes(str, index) {
    // console.log(`getAlphaNumericSubstringIndexes ${str} ${index}`); //`

    if (!StringUtils.isWordishOrNumericChar(str.charAt(index))) return {
      start: 0,
      end: 0
    };
    let start = index;
    // look for the beginning of the substring
    while (start > 0 && StringUtils.isWordishOrNumericChar(str.charAt(start - 1))) --start;
    let end = index;
    // look for the end  of the substring
    while (end < str.length && StringUtils.isWordishOrNumericChar(str.charAt(end))) ++end;
    // start is now the first alphanumeric index
    // end is now the first non-alphanumeric index
    return {
      start: start,
      end: end
    };
  }

  /**
   * Returns number of lines in a string.
   * @param str The string.
   */
  static getNumberOfLines(str) {
    return StringUtils.splitLines(str).length;
  }
  static splitLines(str) {
    return str.split(/\r\n|\r|\n/);
  }

  /**
   * Returns index of found string or -1
   * @param str The string to search.
   * @param reg RegExp to match.
   * @param startIndex Zero-based start index in <code>str</code> to start the search.
   */
  static searchForRegex(str, reg, startIndex) {
    // console.log(":searchForRegex"); //`

    if (startIndex !== 0) str = str.substr(startIndex);
    let ret = str.search(reg);
    if (ret === -1) return -1;else return ret + startIndex;
  }

  /**
   * Sanitizes an HTML string, returning a version that is not susceptible to XSS attacks.
   *
   * Note: HTML elements that are considered unsafe, such as script tags, are removed entirely
   * from the sanitized HTML.
   *
   * @param html A string containing HTML that might not be safe.
   */
  static sanitizeHtmlString(html) {
    return DOMPurify.sanitize(html, {
      FORBID_TAGS: [
      // Disallow forms and all types of input controls (see MH-11981)
      "button", "form", "input", "select", "textarea", "style" // Per MH-27961, we disallow style elements because of possible XSS leaks
      ],
      ADD_ATTR: ["target"] // Allow target attributes to be included in sanitized HTML (see MH-3141)
    });
  }

  /**
   * Checks to see whether a string contains HTML
   */
  static containsHTML(text) {
    const strippedText = DOMPurify.sanitize(text, {
      ALLOWED_TAGS: [],
      RETURN_DOM: false
    });
    return strippedText !== text;
  }

  /**
   * Uses DOMParser to strip HTML tags from a string that presumably contains HTML.
   * @param {string} html HTML string to process
   * @returns {string} Output string stripped of HTML tags.
   */
  static removeHtmlTags(html) {
    if (html.indexOf("<") < 0) {
      // Nothing to do here
      return html;
    }
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
  }
  static replaceSpacesWithMiddot(value) {
    return value != null ? value.replace(/ /g, "\u00B7") : value;
  }

  /**
   * Gelato helper method that replaces a literal "\n" string with a newline.
   * @param {string | null | undefined} s Source string
   * @returns {string | null | undefined} Possibly modified string.
   */
  static parseForNewlineToken(s) {
    if (StringUtils.isNullOrEmpty(s)) {
      return s;
    }
    return s.replace(/\\n/g, "\n");
  }
  static invert(obj) {
    let newObj = {};
    for (const key in obj) newObj[obj[key]] = key;
    return newObj;
  }
  static createEncoder(map) {
    const source = "(?:" + Object.keys(map).join("|") + ")";
    const testRegexp = RegExp(source);
    const replaceRegexp = RegExp(source, "g");
    return function (x) {
      x = x == null ? "" : "" + x;
      return testRegexp.test(x) ? x.replace(replaceRegexp, match => map[match]) : x;
    };
  }

  /**
   * Escapes a string for insertion into HTML, replacing &, <, >, ", ', and ` characters.
   */

  static allEmpty(stringArray) {
    return stringArray.every(str => str === "");
  }
  static prependZeroes(val, desiredLength) {
    return String(val).padStart(desiredLength, "0");
  }

  /**
   * Aggressively replaces potentially invalid characters in a filename
   * Removes/replaces the following characters: \ / : * ? " < > |
   * @param path The filename to remove invalid characters from
   * @param replaceWith (optional) A string to substitute in place of the invalid character
   */
  static removeInvalidFilenameCharacters(path, replaceWith) {
    return path.replace(/[\\/:*?"<>|]/g, replaceWith ?? "");
  }

  /**
   * Squishes all whitespace to a single space, which is the default behavior by browsers when
   * displaying text. Used for working with the string the browser will actually be displaying.
   */

  static reverse(str) {
    return str.split('').reverse().join('');
  }
}
_StringUtils = StringUtils;
_defineProperty(StringUtils, "QUOTE_CHARS", "\"'");
_defineProperty(StringUtils, "ESCAPE_CHAR", "\\");
_defineProperty(StringUtils, "ALL_PRINTABLE", new RegExp("^[\x20-\x7E]+$"));
//"^\\p{Print}+$");
_defineProperty(StringUtils, "HAS_CONTROL_CHARS", new RegExp("^[\x00-\x1F\x7F]+$"));
//"\\p{Cntrl}");
_defineProperty(StringUtils, "HAS_UNESCAPED_QUOTE", new RegExp("[^\\\\]['\"]"));
_defineProperty(StringUtils, "OCTAL", "01234567");
_defineProperty(StringUtils, "makeRegexFromString", (s, matchCase = false) => {
  return new RegExp(_StringUtils.regExpEscape(s), matchCase ? "g" : "gi");
});
_defineProperty(StringUtils, "trimToSpaceSandwich", str => {
  const firstValidCharIdx = str.search(/\S/);
  if (firstValidCharIdx === -1) {
    return "";
  }
  const reverseInput = Array.from(str).reverse().join("");
  const secondValidCharReverseIdx = reverseInput.search(/\S/);
  const secondValidCharIdx = str.length - secondValidCharReverseIdx - 1;
  let start = firstValidCharIdx - 1;
  while (start >= 0) {
    if (str[start] !== " ") {
      break;
    }
    start = start - 1;
  }
  start = start + 1;
  let end = secondValidCharIdx + 1;
  while (end <= str.length - 1) {
    if (str[end] !== " ") {
      break;
    }
    end = end + 1;
  }
  return str.substring(start, end);
});
_defineProperty(StringUtils, "escapeMap", {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  "\"": "&quot;",
  "'": "&#x27;",
  "`": "&#x60;"
});
_defineProperty(StringUtils, "unescapeMap", _StringUtils.invert(_StringUtils.escapeMap));
_defineProperty(StringUtils, "encodeHtml", _StringUtils.createEncoder(_StringUtils.escapeMap));
/**
 * The opposite of {@link encodeHtml}, replaces &amp;, &lt;, &gt;, &quot;, &#x27; and &#x60; with their unescaped counterparts.
 */
_defineProperty(StringUtils, "decodeHtml", _StringUtils.createEncoder(_StringUtils.unescapeMap));
_defineProperty(StringUtils, "normalizeWhitespace", s => {
  return s.trim().replace(/\s+/g, " ");
});
//# sourceMappingURL=StringUtils.js.map
